import { useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Briefcase, CheckCircle2, Upload, ChevronDown, Send, X, Loader2 } from "lucide-react";
import { db } from "../../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

type FormData = {
  name: string;
  phoneNumber: string;
  email: string;
  qualification: string;
  position: string;
  location: string;
  experiences: string;
  resumeURL: File | null;
};

const QUALIFICATIONS = [
  "SPM / O-Level",
  "STPM / A-Level",
  "Foundation",
  "Diploma",
  "Bachelor's Degree",
  "Master's Degree",
  "PhD",
];

const POSITIONS = [
  "Sales Education Consultant (Shah Alam)",
  "Sales Education Consultant (Kuantan)",
];

const LOCATIONS = ["Shah Alam, Selangor", "Kuantan, Pahang", "Kepong, Kuala Lumpur"];

export default function ApplicationModal({
  open,
  onClose,
  prefilledPosition,
  prefilledLocation,
}: {
  open: boolean;
  onClose: () => void;
  prefilledPosition?: string;
  prefilledLocation?: string;
}) {
  const [form, setForm] = useState<FormData>({
    name: "",
    phoneNumber: "",
    email: "",
    qualification: "",
    position: prefilledPosition || "",
    location: prefilledLocation || "",
    experiences: "",
    resumeURL: null,
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const update = (k: keyof FormData, v: string | File | null) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const nameRegex = /^[A-Za-z]+( [A-Za-z]+)*$/;
    if (!nameRegex.test(form.name.trim())) {
      setSubmitError("Name must not contain numbers, symbols, or extra whitespace.");
      return;
    }

    const phoneRegex = /^(0|60)\d+$/;
    if (!phoneRegex.test(form.phoneNumber)) {
      setSubmitError("Phone number must only contain numbers and start with 0 or 60.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setSubmitError("Please enter a valid email address.");
      return;
    }

    if (!form.resumeURL) {
      setSubmitError("please attach your resume");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const storage = getStorage();
      const resumeRef = ref(storage, `resumes/${Date.now()}_${form.resumeURL.name}`);
      const uploadResult = await uploadBytes(resumeRef, form.resumeURL);
      const resumeUrl = await getDownloadURL(uploadResult.ref);

      await addDoc(collection(db, "job-application"), {
        name: form.name,
        phoneNumber: form.phoneNumber,
        email: form.email,
        qualification: form.qualification,
        position: form.position,
        location: form.location,
        experiences: form.experiences,
        resumeUrl: resumeUrl,
        createdAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting application: ", error);
      setSubmitError("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white rounded-[28px] shadow-2xl shadow-slate-900/20 w-full max-w-xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-7 pt-7 pb-5 border-b border-slate-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 mb-3">
                    <Briefcase className="w-3 h-3 text-violet-600" />
                    <span className="text-[11px] text-violet-700 uppercase tracking-wider" style={{ fontWeight: 700 }}>
                      Job Application
                    </span>
                  </div>
                  <h3 className="text-[22px] text-slate-900 tracking-tight" style={{ fontWeight: 900 }}>
                    Apply to SCS Education
                  </h3>
                  <p className="text-[13px] text-slate-500 mt-1" style={{ fontWeight: 400 }}>
                    Fill in the form below and we'll get back to you within 2 business days.
                  </p>
                </div>
                <button onClick={onClose} className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors cursor-pointer">
                  <X className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-5">
                {/* Full Name */}
                <div>
                  <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Full Name *</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Ahmad Faizal bin Abdullah"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[14px] text-slate-800 placeholder-slate-400 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-50 transition-all"
                    style={{ fontWeight: 400 }}
                  />
                </div>

                {/* Phone + Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Phone Number *</label>
                    <input
                      required
                      type="tel"
                      placeholder="60123456789"
                      value={form.phoneNumber}
                      onChange={(e) => update("phoneNumber", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[14px] text-slate-800 placeholder-slate-400 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-50 transition-all"
                      style={{ fontWeight: 400 }}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Email *</label>
                    <input
                      required
                      type="email"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[14px] text-slate-800 placeholder-slate-400 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-50 transition-all"
                      style={{ fontWeight: 400 }}
                    />
                  </div>
                </div>

                {/* Qualification */}
                <div>
                  <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Highest Qualification *</label>
                  <div className="relative">
                    <select
                      required
                      value={form.qualification}
                      onChange={(e) => update("qualification", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[14px] text-slate-800 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-50 transition-all appearance-none cursor-pointer"
                      style={{ fontWeight: 400 }}
                    >
                      <option value="">Select qualification...</option>
                      {QUALIFICATIONS.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Position */}
                <div>
                  <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Interested Job Position *</label>
                  <div className="relative">
                    <select
                      required
                      value={form.position}
                      onChange={(e) => update("position", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[14px] text-slate-800 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-50 transition-all appearance-none cursor-pointer"
                      style={{ fontWeight: 400 }}
                    >
                      <option value="">Select position...</option>
                      {POSITIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Preferred Location */}
                <div>
                  <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Preferred Work Location *</label>
                  <div className="relative">
                    <select
                      required
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[14px] text-slate-800 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-50 transition-all appearance-none cursor-pointer"
                      style={{ fontWeight: 400 }}
                    >
                      <option value="">Select location...</option>
                      {LOCATIONS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Sales Experience */}
                <div>
                  <label className="block text-[12px] text-slate-700 mb-2.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Do you have experience in Sales? *</label>
                  <div className="grid grid-cols-3 gap-2.5">
                    {["Yes", "No", "Some exposure"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => update("experiences", opt)}
                        className={`py-3 rounded-2xl border text-[13px] transition-all duration-150 cursor-pointer ${
                          form.experiences === opt
                            ? "bg-violet-600 border-violet-600 text-white shadow-md shadow-violet-500/25"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-violet-200 hover:bg-violet-50"
                        }`}
                        style={{ fontWeight: form.experiences === opt ? 700 : 500 }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-[12px] text-slate-700 mb-1.5 uppercase tracking-wider" style={{ fontWeight: 700 }}>Upload Resume *</label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => update("resumeURL", e.target.files?.[0] ?? null)}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className={`w-full py-4 rounded-2xl border-2 border-dashed flex flex-col items-center gap-2 transition-all cursor-pointer ${
                      form.resumeURL
                        ? "border-violet-400 bg-violet-50"
                        : "border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-violet-50/50"
                    }`}
                  >
                    {form.resumeURL ? (
                      <>
                        <CheckCircle2 className="w-6 h-6 text-violet-600" />
                        <span className="text-[13px] text-violet-700" style={{ fontWeight: 600 }}>{form.resumeURL.name}</span>
                        <span className="text-[11px] text-violet-500" style={{ fontWeight: 400 }}>Click to replace</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-slate-400" />
                        <span className="text-[13px] text-slate-600" style={{ fontWeight: 600 }}>Click to upload your resume</span>
                        <span className="text-[11px] text-slate-400" style={{ fontWeight: 400 }}>PDF — Max 5MB</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Submit Error */}
                {submitError && (
                  <div className="text-red-500 text-[13px] text-center" style={{ fontWeight: 500 }}>
                    {submitError}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[15px] flex items-center justify-center gap-2 shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:-translate-y-px transition-all mt-1 disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed cursor-pointer"
                  style={{ fontWeight: 700 }}
                >
                  {isSubmitting ? (
                    <>
                      Submitting... <Loader2 className="w-4 h-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Submit Application <Send className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] text-slate-400" style={{ fontWeight: 400 }}>
                  We review all applications within 2 business days. Your data is kept confidential.
                </p>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="px-7 py-14 flex flex-col items-center text-center gap-5"
              >
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-[40px] shadow-2xl shadow-violet-500/30">
                  🎉
                </div>
                <div>
                  <h3 className="text-[24px] text-slate-900 tracking-tight mb-2" style={{ fontWeight: 900 }}>
                    Application Received!
                  </h3>
                  <p className="text-[14px] text-slate-500 leading-relaxed max-w-sm" style={{ fontWeight: 400 }}>
                    Thanks, <span className="text-slate-800" style={{ fontWeight: 600 }}>{form.name || "there"}</span>! Our HR team will review your application and reach out to you at{" "}
                    <span className="text-violet-600" style={{ fontWeight: 600 }}>{form.email || "your email"}</span> within 2 business days.
                  </p>
                </div>
                <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-50 border border-green-100">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[12px] text-green-700" style={{ fontWeight: 600 }}>We're actively hiring right now</span>
                </div>
                <button
                  onClick={onClose}
                  className="px-8 py-3 rounded-2xl border border-slate-200 text-slate-700 text-[14px] hover:bg-slate-50 transition-all cursor-pointer"
                  style={{ fontWeight: 600 }}
                >
                  Close
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}